/**
 * Standard Code CLI entry point.
 *
 * Pre-flight (plain readline): resolve the instance endpoint, OAuth-login if
 * needed, then offer to resume a thread tagged for this project+machine or start
 * a new one. Then hand off to the interactive TUI which streams the agent and
 * forwards its tools to this host.
 */
import os from "node:os";
import fs from "node:fs";
import path from "node:path";
import readline from "node:readline/promises";
import { spawn } from "node:child_process";
import { stdin as input, stdout as output } from "node:process";
import { ApiClient, type MessageAttachment, type SessionQuote, type SkillInfo, type SubagentInfo } from "./api.ts";
import { Bridge } from "./bridge.ts";
import { HostTools } from "./host-tools.ts";
import { MessageStream } from "./stream.ts";
import { SystemEvents } from "./events-stream.ts";
import { SubagentActivity } from "./subagent-streams.ts";
import { Tui, INPUT_BOX_MARGIN, type PastedImage } from "./tui.ts";
import { loadHistory, appendHistory } from "./history.ts";
import { renderMarkdown } from "./markdown.ts";
import { ProcessRegistry } from "./process-registry.ts";
import { McpManager } from "./mcp.ts";
import {
  listMcpServers,
  saveMcpServer,
  removeMcpServer,
  setMcpServerEnabled,
  parseServerSpec,
  type McpServerConfig,
} from "./mcp-config.ts";
import {
  getCredential,
  saveCredential,
  deleteCredential,
  defaultEndpoint,
  normalizeEndpoint,
  saveDefaultEndpoint,
} from "./credentials.ts";
import { LEVELS, levelLabel, type Level, type ThreadSummary } from "./types.ts";
import type { PermissionState } from "./permissions.ts";
import { loadApprovals, saveApprovals } from "./approvals.ts";
import { gradientText, brandCycleColor } from "./render.ts";
import {
  checkForUpdate,
  consumeAppliedUpdate,
  decideAutoUpdate,
  detectPackageManager,
  forceCheckForUpdate,
  readAutoUpdateState,
  runUpdate,
  startBackgroundUpdate,
  updateCommand,
  type UpdateInfo,
} from "./check-update.ts";

const AGENT_ID = "standard_code_agent";
// The instance's model gearbox hands sessions between agent variants; a
// thread parked in another gear must still be resumable from the CLI.
const AGENT_ID_VARIANTS = [AGENT_ID, "standard_code_high_agent"];
// The hosted Standard Code instance — where every normal user's session
// lives. Only `--endpoint` (rare: local dev, self-hosted) points elsewhere.
const PRODUCTION_ENDPOINT = "https://api.standardcode.ai";

const c = {
  reset: "\x1b[0m",
  dim: "\x1b[2m",
  bold: "\x1b[1m",
  white: "\x1b[97m",
  cyan: "\x1b[36m",
  green: "\x1b[32m",
  gray: "\x1b[90m",
  magenta: "\x1b[35m",
  yellow: "\x1b[33m",
  red: "\x1b[31m",
  teal: "\x1b[38;5;37m", // brand teal (matches the marketing site's teal accent)
};

/**
 * The Standard Agents logomark — two interlocking squares crossed by a diagonal
 * lens — rendered in full block characters. Printed at the left of the startup
 * banner with the session details aligned to its right.
 */
const LOGO_MARK = [
  "     █████████████",
  "     ███        ██",
  "     ██          █",
  "████ ████████   ██",
  "███  ████████  ███",
  "██   ████████ ████",
  "█          ██",
  "██        ███",
  "█████████████",
];

interface CliArgs {
  endpoint?: string;
  promptEndpoint: boolean;
  dir?: string;
  help: boolean;
}

function printUsage(): void {
  output.write(
    [
      "",
      `${c.bold}Usage${c.reset}`,
      "  standardcode [options] [dir]",
      "",
      `${c.bold}Options${c.reset}`,
      "  -e, --endpoint [url]  Use a different Standard Agents instance for this run",
      "                       (default: https://api.standardcode.ai).",
      "                       If url is omitted, prompt for it.",
      "                       Credentials are remembered for that endpoint, but",
      "                       the saved default endpoint is not changed.",
      "  -h, --help            Show this help.",
      "",
    ].join("\n")
  );
}

function parseArgs(args: string[]): CliArgs {
  const parsed: CliArgs = { help: false, promptEndpoint: false };
  for (let i = 0; i < args.length; i++) {
    const arg = args[i];
    if (arg === "--help" || arg === "-h") {
      parsed.help = true;
      continue;
    }
    if (arg === "--endpoint" || arg === "-e") {
      const value = args[i + 1];
      if (value && !value.startsWith("-")) {
        parsed.endpoint = value;
        i++;
      } else {
        parsed.promptEndpoint = true;
      }
      continue;
    }
    if (arg.startsWith("--endpoint=")) {
      const value = arg.slice("--endpoint=".length);
      if (value) {
        parsed.endpoint = value;
      } else {
        parsed.promptEndpoint = true;
      }
      continue;
    }
    if (arg === "--") {
      // Some package-manager script invocations pass their separator through to
      // the script (for example `pnpm code -- --endpoint ...`). Treat a leading
      // separator before another option as a wrapper artifact.
      if (i === 0 && args[i + 1]?.startsWith("-")) continue;
      const rest = args.slice(i + 1);
      if (rest.length > 1) throw new Error("Expected at most one project directory.");
      if (rest[0]) parsed.dir = rest[0];
      break;
    }
    if (arg.startsWith("-")) throw new Error(`Unknown option: ${arg}`);
    if (parsed.dir) throw new Error("Expected at most one project directory.");
    parsed.dir = arg;
  }
  return parsed;
}

/**
 * Print an assistant reply: light Markdown formatting, padded with a blank line
 * above and below so successive messages don't run together. A small grey dot
 * marks the first line in the gutter — scanning the left edge shows where each
 * new response begins — with the body indented to align under it.
 */
function printAssistant(tui: Tui, text: string): void {
  const cols = Math.max(20, (process.stdout.columns || 80) - 3); // cushion for the 2-col gutter
  tui.clearStream(); // wipe the live preview; the committed message renders below
  tui.print("");
  let dotted = false;
  for (const line of renderMarkdown(text, cols)) {
    if (!dotted && line.trim()) {
      tui.print(`${c.gray}•${c.reset} ${line}`);
      dotted = true;
    } else {
      tui.print(`  ${line}`);
    }
  }
  tui.print("");
}

/**
 * A single-line animated loading indicator for the pre-TUI phases (verifying
 * credentials, listing sessions, attaching to the thread). Redraws in place on
 * the current line; `stop()` wipes it so the next output starts clean. Safe
 * only while nothing else writes to stdout. Indented to match the boxed HUD's
 * outer margin so the pre-flight stack lines up with the input box.
 */
function startLoader(label: string): { stop: () => void } {
  const frames = ["⣷", "⣯", "⣟", "⡿", "⢿", "⣻", "⣽", "⣾"];
  const pad = " ".repeat(INPUT_BOX_MARGIN);
  output.write("\x1b[?25l"); // hide the cursor while the glyph animates
  const draw = () => {
    const now = Date.now();
    const f = frames[Math.floor(now / 70) % frames.length];
    output.write(`\r\x1b[K${pad}${brandCycleColor(now)}${f}${c.reset} ${c.dim}${label}…${c.reset}`);
  };
  draw();
  const timer = setInterval(draw, 70);

  return {
    stop: () => {
      clearInterval(timer);
      output.write("\r\x1b[K\x1b[?25h");
    },
  };
}

/** A calm, branded goodbye printed on a graceful shutdown. */
function farewell(stoppedProcs = 0): void {
  output.write("\x1b[?25h"); // re-show the cursor in case a loader had it hidden
  if (stoppedProcs > 0) {
    output.write(
      `\n${c.cyan}⚙${c.reset} Stopped ${stoppedProcs} background process${stoppedProcs === 1 ? "" : "es"}.\n`
    );
  }
  output.write(`\n${c.teal}◇${c.reset} ${c.dim}Standard Code — see you soon.${c.reset}\n`);
}

/** Whether a hostname is a local/private dev address (loopback, .local, RFC-1918). */
function isLocalHost(host: string): boolean {
  return (
    host === "localhost" ||
    host === "127.0.0.1" ||
    host === "::1" ||
    host.endsWith(".local") ||
    host.endsWith(".localhost") ||
    /^10\./.test(host) ||
    /^192\.168\./.test(host) ||
    /^172\.(1[6-9]|2\d|3[01])\./.test(host)
  );
}

/**
 * Relax TLS verification for local/private endpoints only. OrbStack (and other
 * local dev setups) serve HTTPS with a CA installed in the system keychain —
 * which curl and browsers trust, but Node's `fetch`/`WebSocket` do not, so they
 * fail with SELF_SIGNED_CERT_IN_CHAIN and every token "doesn't work". For a
 * loopback/`.local`/RFC-1918 host we disable verification (covering fetch and
 * the bridge/stream WebSockets); public endpoints keep full verification.
 * Returns true when verification was relaxed.
 */
function relaxTlsForLocalEndpoint(endpoint: string): boolean {
  let host = "";
  try {
    host = new URL(endpoint).hostname;
  } catch {
    return false;
  }
  if (!endpoint.startsWith("https:") || !isLocalHost(host)) return false;
  // Filter out Node's global "insecure TLS" warning; we surface our own note.
  const origEmit = process.emitWarning.bind(process);
  process.emitWarning = ((warning: string | Error, ...args: unknown[]) => {
    const msg = typeof warning === "string" ? warning : warning?.message ?? "";
    if (msg.includes("NODE_TLS_REJECT_UNAUTHORIZED")) return;
    return (origEmit as (...a: unknown[]) => void)(warning, ...args);
  }) as typeof process.emitWarning;
  process.env.NODE_TLS_REJECT_UNAUTHORIZED = "0";
  return true;
}

/** Read this package's version from package.json (best effort). */
function readVersion(): string {
  try {
    const pkg = JSON.parse(fs.readFileSync(new URL("../package.json", import.meta.url), "utf8"));
    return typeof pkg.version === "string" ? pkg.version : "";
  } catch {
    return "";
  }
}

/**
 * Print the startup welcome: the logomark, then the product name, version, the
 * connected instance, and the working directory stacked beneath it — a calm,
 * branded header before the session begins.
 */
function printWelcome(endpoint: string, projectDir: string): void {
  const home = os.homedir();
  const dir = projectDir.startsWith(home) ? "~" + projectDir.slice(home.length) : projectDir;
  const host = endpoint.replace(/^https?:\/\//, "").replace(/\/$/, "");
  const version = readVersion();
  const pad = "  ";
  // Session details, placed to the right of the mark and vertically centered.
  // The hosted instance is the product's home — naming its host on the splash
  // is just noise, so the endpoint line only appears for non-default instances.
  const meta = [
    `${c.bold}${gradientText("Standard Code")}${c.reset}${version ? `  ${c.dim}v${version}${c.reset}` : ""}`,
    `${c.dim}terminal coding agent${c.reset}`,
    ...(endpoint === PRODUCTION_ENDPOINT ? [] : [`${c.teal}${host}${c.reset}`]),
    `${c.dim}${dir}${c.reset}`,
  ];
  const markWidth = Math.max(...LOGO_MARK.map((l) => [...l].length));
  const metaTop = Math.floor((LOGO_MARK.length - meta.length) / 2);
  output.write("\n");
  for (let i = 0; i < LOGO_MARK.length; i++) {
    const glyph = LOGO_MARK[i].padEnd(markWidth); // logo stays white-on-black
    const line = meta[i - metaTop];
    output.write(`${pad}${glyph}${line ? `   ${line}` : ""}\n`);
  }
  output.write("\n");
}

/**
 * Colour a tool-activity line for readable contrast: a bright status glyph
 * (green ✓, red ✗, yellow ⛔) with the action text at full contrast and only the
 * trailing parenthetical detail dimmed. Replaces the old wrap-everything-in-gray.
 */
function colorActivity(line: string): string {
  const m = line.match(/^(\s*)([✓✗⛔])\s?([\s\S]*)$/);
  if (!m) return `${c.dim}${line}${c.reset}`;
  const [, indent, glyph, rest] = m;
  if (glyph === "✓") {
    const body = rest.replace(/\s(\([^()]*\))\s*$/, ` ${c.dim}$1${c.reset}`);
    return `${indent}${c.green}✓${c.reset} ${body}`;
  }
  if (glyph === "✗") {
    // Error output (e.g. a failed command's stderr) rides along in `rest` and can
    // run dozens of lines. Show it in red and cap it at a handful of lines so the
    // transcript stays scannable — the model still receives the full untruncated
    // result over the bridge, this only trims what the human sees.
    const ERR_MAX_LINES = 7;
    const lines = rest.split("\n");
    const shown = lines.slice(0, ERR_MAX_LINES);
    const hidden = lines.length - shown.length;
    const body = shown
      .map((l, i) =>
        i === 0
          ? `${indent}${c.red}✗ ${l}${c.reset}`
          : `${indent}${c.red}${c.dim}${l}${c.reset}`
      )
      .join("\n");
    if (hidden > 0) {
      return `${body}\n${indent}${c.dim}… +${hidden} more line${hidden === 1 ? "" : "s"}${c.reset}`;
    }
    return body;
  }
  return `${indent}${c.yellow}⛔ ${rest}${c.reset}`;
}

async function main(): Promise<void> {
  let cliArgs: CliArgs;
  try {
    cliArgs = parseArgs(process.argv.slice(2));
  } catch (error) {
    output.write(`${c.red}error:${c.reset} ${error instanceof Error ? error.message : String(error)}\n`);
    printUsage();
    process.exit(1);
  }
  if (cliArgs.help) {
    printUsage();
    return;
  }
  const endpointArg = cliArgs.endpoint;
  const endpointOverride = cliArgs.promptEndpoint || (typeof endpointArg === "string" && endpointArg.trim() !== "");
  const dirArg = cliArgs.dir;

  const projectDir = path.resolve(dirArg || process.cwd());
  const machine = os.hostname();

  // Create the line reader lazily so it never eagerly consumes stdin (which
  // would swallow a message the user pre-types before the interactive prompt).
  // A `SIGINT` listener implements double-press-to-quit during these pre-flight
  // prompts — without it, ctrl-c rejects `question()` with an AbortError and
  // crashes the process with a stack trace.
  const reader: { rl: readline.Interface | null } = { rl: null };
  let handoffClosing = false; // set when we intentionally close the reader for the TUI handoff
  let preflightArmed = false;
  let preflightTimer: ReturnType<typeof setTimeout> | null = null;
  const onPreflightSigint = () => {
    if (preflightArmed) {
      if (preflightTimer) clearTimeout(preflightTimer);
      reader.rl?.close();
      farewell();
      process.exit(0);
    }
    preflightArmed = true;
    output.write(`\n${c.dim}Press Control-C again to exit${c.reset}\n`);
    preflightTimer = setTimeout(() => {
      preflightArmed = false;
      preflightTimer = null;
    }, 2000);
  };
  const ask = async (question: string): Promise<string> => {
    if (!reader.rl) {
      reader.rl = readline.createInterface({ input, output });
      reader.rl.on("SIGINT", onPreflightSigint);
      // EOF (ctrl-d / closed stdin) ends the reader — exit gracefully instead of
      // looping into an ERR_USE_AFTER_CLOSE on the next prompt.
      reader.rl.on("close", () => {
        if (handoffClosing) return; // our own handoff close, not the user leaving
        farewell();
        process.exit(0);
      });
    }
    return reader.rl.question(question);
  };
  const askEndpoint = async (): Promise<string> => {
    for (;;) {
      const answer = (await ask(
        `${c.cyan}Standard Agents instance URL${c.reset} (e.g. http://localhost:5178): `
      )).trim();
      if (answer) return answer;
      output.write(`${c.dim}An endpoint URL is required.${c.reset}\n`);
    }
  };
  // Cover the cooked-mode gaps too (e.g. while resolving threads before the TUI
  // grabs the keyboard) so a stray ctrl-c there also takes two presses and exits
  // gracefully rather than killing the process. Once the TUI enables raw mode,
  // ctrl-c arrives as a keypress and this never fires.
  process.on("SIGINT", onPreflightSigint);

  // 1. Resolve endpoint. Normal users never choose one: the hosted Standard
  //    Code instance is the default, and the very first thing they ever see is
  //    the splash — not a URL prompt. `--endpoint <url>` overrides for a run;
  //    bare `--endpoint` explicitly asks; a previously saved default (set by
  //    an explicit endpoint flow) is respected for power users.
  let endpointPrompted = false;
  let endpoint = endpointArg || (cliArgs.promptEndpoint ? "" : defaultEndpoint() || PRODUCTION_ENDPOINT);
  if (!endpoint) {
    endpoint = await askEndpoint();
    endpointPrompted = true;
  }
  endpoint = normalizeEndpoint(endpoint);

  // Local dev instances (OrbStack, etc.) use a CA Node won't trust by default —
  // relax TLS for loopback/.local/private hosts so the CLI can actually connect.
  const tlsRelaxed = relaxTlsForLocalEndpoint(endpoint);

  // Welcome banner — printed once the destination instance is known, before any
  // auth prompt, so the session opens with a calm, branded header.
  printWelcome(endpoint, projectDir);
  if (tlsRelaxed) {
    output.write(`${c.dim}  TLS verification relaxed for local endpoint.${c.reset}\n\n`);
  }

  // 3. Check for available updates (fast, cached — silently skipped if
  //    offline). When the CLI is an installed copy, a newer version installs
  //    itself in a detached background process — via the SAME package manager
  //    that owns the install — and applies on the next launch. A dev checkout,
  //    an opted-out env, or a previously failed attempt just shows the hint.
  const version = readVersion();
  let updateAvailable: UpdateInfo | null = null;
  {
    const loading = startLoader("Checking for updates");
    updateAvailable = await checkForUpdate(version);
    loading.stop();
    const applied = consumeAppliedUpdate(version);
    if (applied) {
      output.write(`  ${c.green}✓${c.reset} ${c.dim}Standard Code updated to v${version}.${c.reset}\n\n`);
    }
    if (updateAvailable) {
      const pm = detectPackageManager();
      const decision = decideAutoUpdate(updateAvailable, { state: readAutoUpdateState(), pm });
      if (decision === "start" && pm && startBackgroundUpdate(updateAvailable.latest, pm)) {
        output.write(
          `  ${c.teal}⟳${c.reset} ${c.dim}Standard Code ${c.reset}${c.bold}v${updateAvailable.latest}${c.reset}${c.dim} is installing in the background — it applies on your next launch.${c.reset}\n\n`
        );
      } else if (decision === "in_flight") {
        output.write(
          `  ${c.teal}⟳${c.reset} ${c.dim}Standard Code v${updateAvailable.latest} is still installing in the background.${c.reset}\n\n`
        );
      } else {
        const display = updateCommand(pm ?? "npm").display;
        output.write(
          `  ${c.teal}◇${c.reset} ${c.dim}Update available:${c.reset} ${c.dim}v${updateAvailable.current}${c.reset} → ${c.bold}v${updateAvailable.latest}${c.reset}\n` +
            `  ${c.dim}Run ${c.reset}${c.bold}${display}${c.reset}${c.dim} to update${c.reset}\n\n`
        );
      }
    }
  }

  // 2. Resolve credentials. Use a stored API token if it still works, otherwise
  //    ask for one (create it in your Standard Agents instance settings).
  const stored = getCredential(endpoint);
  let api = stored ? new ApiClient(endpoint, stored.access_token) : null;
  let storedCheck: Awaited<ReturnType<ApiClient["verifyDetailed"]>> | null = null;
  if (api) {
    const connecting = startLoader("Connecting to Standard Agents");
    storedCheck = await api.verifyDetailed();
    connecting.stop();
  }
  if (!api || !storedCheck?.ok) {
    const host = endpoint.replace(/^https?:\/\//, "").replace(/\/$/, "");
    // Say WHY the saved credentials didn't carry over — a stopped server or a
    // dead network is a very different situation from a revoked key, and the
    // user shouldn't be pasting fresh tokens at a connection problem.
    if (storedCheck && !storedCheck.ok) {
      output.write(`${c.red}✗${c.reset} ${c.dim}Saved sign-in for this endpoint failed:${c.reset} ${storedCheck.reason}\n`);
      if (storedCheck.hint) output.write(`  ${c.dim}${storedCheck.hint}${c.reset}\n`);
      output.write("\n");
    }
    const explainFailure = (result: { reason: string; hint?: string }, prefix: string) => {
      output.write(`${c.red}✗${c.reset} ${prefix}${result.reason}\n`);
      if (result.hint) output.write(`  ${c.dim}${result.hint}${c.reset}\n`);
    };
    output.write(`${c.bold}${gradientText("Sign in to Standard Code")}${c.reset}\n`);
    // The hosted instance is the default home; only surface the raw host when
    // the session points somewhere non-standard, so first-run stays clean.
    if (`https://${host}` !== PRODUCTION_ENDPOINT) {
      output.write(`${c.dim}Connecting to${c.reset} ${c.teal}${host}${c.reset}\n`);
    }
    output.write(
      `${c.dim}You'll only need to do this once on this machine.${c.reset}\n\n`
    );
    output.write(
      `${c.white}Press ${c.bold}Enter${c.reset}${c.white} to open your browser and sign in.${c.reset} ${c.dim}(or paste an API token)${c.reset}\n\n`
    );
    for (;;) {
      const token = (await ask(`${c.teal}❯${c.reset} `)).trim();
      if (!token) {
        // Device-code browser login: the instance parks a one-time code, the
        // browser approval (on hosted instances, routed through the Standard
        // Agents platform login) mints a personal API key, and we poll it up.
        const got = await deviceLogin(endpoint).catch((e) => {
          output.write(`${c.red}✗${c.reset} ${c.dim}${e instanceof Error ? e.message : String(e)}${c.reset}\n`);
          return null;
        });
        if (!got) continue;
        api = new ApiClient(endpoint, got);
        const connecting = startLoader("Connecting to Standard Agents");
        const check = await api.verifyDetailed();
        connecting.stop();
        if (check.ok) {
          saveCredential(
            { endpoint, access_token: got, token_type: "Bearer", saved_at: Date.now() },
            { updateDefault: !endpointOverride }
          );
          output.write(`${c.green}✓${c.reset} Connected to ${c.teal}${host}${c.reset}\n`);
          break;
        }
        explainFailure(check, "Browser sign-in didn't verify: ");
        continue;
      }
      api = new ApiClient(endpoint, token);
      const connecting = startLoader("Connecting to Standard Agents");
      const check = await api.verifyDetailed();
      connecting.stop();
      if (check.ok) {
        saveCredential(
          { endpoint, access_token: token, token_type: "Bearer", saved_at: Date.now() },
          { updateDefault: !endpointOverride }
        );
        output.write(`${c.green}✓${c.reset} Connected to ${c.teal}${host}${c.reset}\n`);
        break;
      }
      explainFailure(check, "");
    }
  } else if (endpointPrompted) {
    saveDefaultEndpoint(endpoint);
  }

  if (!api) process.exit(1);
  handoffClosing = true;
  reader.rl?.close();

  // 3. Offer to resume a session for this project+machine, or start new.
  const tags = [`path:${projectDir}`, `machine:${machine}`];
  const loadingSessions = startLoader("Loading sessions");
  let existing: ThreadSummary[] = [];
  try {
    existing = await api.listThreads(AGENT_ID_VARIANTS, tags);
  } catch {
    existing = [];
  }
  const summaries = existing.length > 0 ? await summarizeThreads(api, existing.slice(0, 8)) : [];
  loadingSessions.stop();

  const tui = new Tui(1); // auto-accept level 1 by default (asks on anything riskier than a read)

  let threadId: string;
  let resumed = false;
  // For a brand-new session, ↑/↓ recall seeds from the project's newest prior
  // thread (history lives in the thread KV, not on the local filesystem).
  let historySeed: string | undefined;
  if (existing.length > 0) {
    const items: { label: string; hint?: string; value: string | null }[] = summaries.map((s) => ({
      label: s.label,
      hint: s.hint,
      value: s.id,
    }));
    items.push({ label: "＋ Start a new session", value: null });
    const home = os.homedir();
    const tilde = projectDir.startsWith(home) ? "~" + projectDir.slice(home.length) : projectDir;
    const shortDir = tilde.length > 38 ? "…" + tilde.slice(-37) : tilde;
    const picked = await tui.select(
      `${c.bold}${gradientText("Resume a session")}${c.reset}  ${c.gray}${shortDir}${c.reset}  ${c.dim}↑↓ · enter · esc${c.reset}`,
      items
    );
    if (typeof picked === "string") {
      threadId = picked;
      resumed = true;
    } else {
      threadId = await api.createThread(AGENT_ID, tags);
      historySeed = existing[0]?.id;
    }
  } else {
    threadId = await api.createThread(AGENT_ID, tags);
  }

  // `/clear` resolves the session with a request for a fresh thread; loop so
  // the new session reuses the same TUI, credentials, and project tags. The
  // old thread seeds ↑/↓ recall so input history survives the clear.
  for (;;) {
    await runInteractive(tui, api, threadId, projectDir, machine, resumed, historySeed);
    historySeed = threadId;
    threadId = await api.createThread(AGENT_ID, tags);
    // Tell the fresh thread which thread it replaced so the instance releases
    // that thread's simultaneous-thread slot on the new thread's first step
    // (rather than the slot lingering for its ~90s lease TTL after `/clear`).
    await api.kvSet(threadId, "lease_supersedes", historySeed);
    resumed = false;
  }
}

/**
 * A message is "silent" when it's a system-injected user turn (subagent result
 * hand-backs, malformed-tool-call replays, queued housekeeping) rather than
 * something the human actually typed — so it must never name a session.
 */
function isSilentMessage(m: any): boolean {
  return m?.silent === true || m?.metadata?.silent === true;
}

/**
 * Build a one-line preview + timestamp for each thread, naming the session by
 * the LAST thing the human actually said. We read content via `messageText`
 * (user messages can be content-block arrays, not just strings — the old
 * string-only check made every such session render "(empty session)"), skip
 * silent/system-injected turns, and take the most recent one.
 */
async function summarizeThreads(
  api: ApiClient,
  threads: ThreadSummary[]
): Promise<{ id: string; label: string; hint: string }[]> {
  return Promise.all(
    threads.map(async (t) => {
      let preview = "";
      try {
        // Newest-first so the most recent human message is in-window even for
        // long sessions; we also sort ourselves in case the server ignores it.
        const msgs = await api.getMessages(t.id, 50, "desc");
        const latest = msgs
          .filter((m: any) => m.role === "user" && m.status !== "pending" && !isSilentMessage(m))
          .map((m: any) => ({ at: m.created_at ?? 0, text: messageText(m.content).replace(/\s+/g, " ").trim() }))
          .filter((m) => m.text.length > 0)
          .sort((a, b) => b.at - a.at)[0];
        if (latest) preview = latest.text;
      } catch {
        // ignore; show a placeholder
      }
      const label = preview
        ? preview.length > 64
          ? preview.slice(0, 63) + "…"
          : preview
        : "(empty session)";
      const when = t.created_at ? relativeTime(t.created_at) : "";
      const hint = [t.id.slice(0, 8), when].filter(Boolean).join(" · ");
      return { id: t.id, label, hint };
    })
  );
}

/**
 * A human-readable label for a subagent: prefer its registry title (e.g.
 * "Research Assistant"), else the agent's display title, else a title-cased
 * agent name. A per-instance thread name refines it ("Research Assistant ·
 * auth-flow") when present.
 */
function subagentLabel(s: SubagentInfo, titles: Map<string, string>): string {
  const agentName = (s.agent_name || "").trim();
  const title =
    (s.title || "").trim() ||
    titles.get(agentName) ||
    (agentName ? agentName.replace(/[_-]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "Subagent");
  const tagged = (s.threadName || "").trim();
  return tagged ? `${title} · ${tagged}` : title;
}

/**
 * Device-code browser sign-in: start a device login on the instance, open the
 * approval URL in the browser (hosted instances route an anonymous visit
 * through the Standard Agents platform login first), and poll until the
 * approval mints our personal API key. Throws with a plain message on
 * timeout or failure.
 */
async function deviceLogin(endpoint: string): Promise<string> {
  const start = await fetch(`${endpoint}/api/auth/device/start`, { method: "POST" });
  if (!start.ok) throw new Error(`This instance does not support browser sign-in (HTTP ${start.status}). Paste an API token instead.`);
  const info = (await start.json()) as { code: string; verify_url: string; poll_url: string; interval?: number; expires_in?: number };
  // Wrangler-style: the URL is printed FIRST (copy-pasteable if the browser
  // doesn't open — SSH sessions, containers), then we try to open it.
  output.write(`${c.dim}Opening your browser to sign in. If it doesn't open, visit:${c.reset}\n`);
  output.write(`\n  ${c.teal}${info.verify_url}${c.reset}\n\n`);
  output.write(`${c.dim}Waiting for sign-in to complete… (Ctrl-C to cancel)${c.reset}\n`);
  openUrl(info.verify_url);
  const deadline = Date.now() + (info.expires_in ?? 600) * 1000;
  const interval = Math.max(2, info.interval ?? 2) * 1000;
  while (Date.now() < deadline) {
    await new Promise((r) => setTimeout(r, interval));
    const res = await fetch(info.poll_url).catch(() => null);
    if (!res) continue;
    if (res.status === 404) throw new Error("The sign-in link expired. Try again.");
    const body = (await res.json().catch(() => ({}))) as { status?: string; token?: string };
    if (body.status === "approved" && body.token) return body.token;
    if (body.status === "denied") throw new Error("Sign-in was denied in the browser.");
  }
  throw new Error("Timed out waiting for browser approval. Try again.");
}

/** Open a URL in the system browser (best effort). */
function openUrl(url: string): void {
  const platform = process.platform;
  const cmd = platform === "darwin" ? "open" : platform === "win32" ? "cmd" : "xdg-open";
  const args = platform === "win32" ? ["/c", "start", "", url] : [url];
  try {
    const child = spawn(cmd, args, { stdio: "ignore", detached: true });
    child.unref();
  } catch {
    // ignore — the URL is also printed for manual opening
  }
}

/** Compact relative time from a unix-seconds timestamp. */
function relativeTime(unixSeconds: number): string {
  const diff = Date.now() / 1000 - unixSeconds;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

/**
 * Whether a message carries tool calls. The API serializes `tool_calls` as a
 * JSON STRING (not an array), so `Array.isArray` is never true — checking only
 * that would treat a tool-call assistant message (which can also have narration
 * content) as a final answer, completing the turn while a tool/approval is still
 * pending. Handle both shapes.
 */
function hasToolCalls(m: any): boolean {
  const tc = m?.tool_calls;
  if (Array.isArray(tc)) return tc.length > 0;
  if (typeof tc === "string") {
    const s = tc.trim();
    return s.length > 0 && s !== "null" && s !== "[]";
  }
  return false;
}

/** Extract plain text from a message's content (string or content-block array). */
function messageText(content: unknown): string {
  if (typeof content === "string") return content;
  if (Array.isArray(content)) {
    return content
      .map((b: any) => (typeof b === "string" ? b : typeof b?.text === "string" ? b.text : ""))
      .join("");
  }
  return "";
}

/** Whether the thread is currently working (a turn active or queued work pending). */
function threadBusy(msgs: any[]): boolean {
  if (!msgs.length) return false;
  if (msgs.some((m) => m.status === "pending")) return true;
  const last = [...msgs].sort((a, b) => (b.created_at ?? 0) - (a.created_at ?? 0))[0];
  if (!last) return false;
  if (last.role === "user" || last.role === "tool") return true; // turn about to run / mid-turn
  if (last.role === "assistant") return hasToolCalls(last); // tool-call message = still going
  return false; // a final assistant text, or a system/stop message → idle
}

/** Replay a resumed thread's conversation so it feels like picking up mid-session. */
async function printHistory(api: ApiClient, threadId: string, tui: Tui): Promise<void> {
  let msgs: any[];
  try {
    msgs = await api.getMessages(threadId, 200);
  } catch {
    return;
  }
  const convo = msgs
    .filter((m) => m.role === "user" || (m.role === "assistant" && messageText(m.content).trim()))
    .sort((a, b) => (a.created_at ?? 0) - (b.created_at ?? 0));
  if (!convo.length) return;
  const shown = convo.slice(-24);
  tui.print(`${c.dim}── resuming session · ${convo.length} message${convo.length === 1 ? "" : "s"} ──${c.reset}`);
  if (shown.length < convo.length) tui.print(`${c.dim}   … earlier messages omitted${c.reset}`);
  for (const m of shown) {
    const text = messageText(m.content).trim();
    if (!text) continue;
    if (m.role === "user") tui.printUserMessage(text);
    else printAssistant(tui, text);
  }
}

async function runInteractive(
  tui: Tui,
  api: ApiClient,
  threadId: string,
  projectDir: string,
  machine: string,
  resumed: boolean,
  historySeedThreadId?: string
): Promise<void> {
  // Background-process metadata lives in the thread's KV (server-side), so the
  // list resumes from any machine; nothing about it is stored on this client.
  const registry = new ProcessRegistry(api, threadId, machine);
  // MCP host: the CLI negotiates with locally-launched MCP servers and forwards
  // the agent's `mcp` tool calls to them. The discovered tool catalog is mirrored
  // into the thread KV so a context hook can show the agent what's available.
  const mcp = new McpManager(projectDir);
  const publishMcpCatalog = () => void api.kvSet(threadId, "mcp_catalog", mcp.catalog()).catch(() => {});
  const host = new HostTools(projectDir, registry, threadId, machine, mcp, publishMcpCatalog, api);
  const refreshBgCount = () => {
    void registry.runningCount().then((n) => tui.setBackgroundCount(n)).catch(() => {});
  };
  const perm: PermissionState = { level: tui.level, alwaysAllow: new Set(), allowRisk: new Set() };
  // Restore this session's saved approvals (auto-accept level + pre-approved
  // tools and risk levels) from the thread KV, so they resume with the session.
  const savedApprovals = await loadApprovals(api, threadId);
  for (const t of savedApprovals.allowTools) perm.alwaysAllow.add(t);
  for (const r of savedApprovals.allowRisk) perm.allowRisk.add(r);
  if (savedApprovals.level) {
    perm.level = savedApprovals.level;
    tui.setLevel(savedApprovals.level);
  }
  tui.onLevelChange((l: Level) => {
    perm.level = l;
    saveApprovals(api, threadId, perm);
  });
  // Persist the effective permission setting now (not just on change) so the
  // backend evaluates the SAME level/approvals when deciding whether a forwarded
  // tool needs approval — otherwise it parks calls the CLI would auto-accept.
  saveApprovals(api, threadId, perm);
  // Tell the agent where its shell lives: the instance's context hook renders
  // this as a synthetic opening pwd exchange + a [SESSION] line, so the agent
  // never burns a bash call (or a cd prefix) discovering its own cwd.
  void api.kvSet(threadId, "session_info", { cwd: projectDir, machine }).catch(() => {});

  // Everything from here to the banner is session wiring (history load, the
  // bridge/stream/events sockets) — show one loader for the whole stretch.
  // Started after the approvals restore above so its possible level-change
  // print can't collide with the redrawn loader line.
  const attaching = startLoader("Attaching to thread");

  // ── state ────────────────────────────────────────────────────────────────
  let busy = false; // is the agent currently working
  // Set when the user hits esc to interrupt: clears the working indicator
  // immediately and suppresses the poller from re-showing it until the backend
  // confirms the turn actually stopped (otherwise "Working" lingers a poll or two).
  let interrupting = false;
  // Messages typed while busy, held to send next (with any pasted images).
  const queued: { text: string; images: PastedImage[] }[] = [];
  let editingQueued = false; // input currently holds a pulled queued message
  const shownIds = new Set<string>(); // message ids already printed to the transcript
  // User messages the CLI optimistically displayed on send, keyed by content, so
  // the poller doesn't re-print them — while it DOES render user messages that
  // were submitted from elsewhere (web UI, API, another client).
  const pendingSent = new Map<string, number>();
  // The last message we sent — resent verbatim to continue after an at-limit
  // upgrade (the blocked turn never reached the model).
  let lastSent: { text: string; images: PastedImage[] } | null = null;
  let upgradeInFlight = false;

  // ── token feed ──────────────────────────────────────────────────────────
  // Cumulative tokens are authoritative from logs (one row per completed LLM
  // call); a live estimate from streamed content ticks the output count up
  // between log updates so a long generation isn't silent.
  let tokensIn = 0;
  let tokensOut = 0;
  let liveOut = 0; // output tokens for the in-progress response (from `generation` events)
  const countedLogs = new Set<string>();

  // ── step indicator (what the agent is doing right now) ────────────────────
  // The label comes from the server stream's tool_call_started/done events — it
  // shows what the agent is doing, which is especially valuable for a long tool.
  // That signal is best-effort though (a reconnect can drop an event), so the
  // bridge — which actually runs the forwarded tools on the host — RECONCILES it:
  // it fills in a `started` the stream missed and clears a `done` it dropped, so
  // the label can't strand on a tool that already finished (see Bridge onStatus).
  // Most recently started active tool wins; cleared when the turn goes idle.
  const activeSteps = new Map<string, string>(); // tool call id -> label

  // Push the current totals + step to the prompt. Output total folds in the
  // live in-progress count; the step's own output is the live count.
  const refreshStatus = () => {
    tui.setTokens(tokensIn, tokensOut + liveOut);
    let label: string | null = null;
    for (const v of activeSteps.values()) label = v; // last (most recent) entry
    tui.setStep(label, liveOut);
  };

  const bridge = new Bridge(api, threadId, host, perm, {
    onActivity: (line, detail) => {
      tui.print(colorActivity(line));
      // Pre-styled extra rows (truncated diffs for edits/writes) print verbatim.
      if (detail) for (const d of detail) tui.print(d);
      refreshBgCount();
    },
    onStatus: (id, summary) => {
      // Reconcile the stream's best-effort step signal with what the host is
      // really running — the CLI executes the forwarded tools, so it knows:
      //  - on start: fill in a `started` the stream may have dropped, but DON'T
      //    override a label the stream already set (it can be richer/intentional).
      //  - on finish: authoritatively clear it, healing a dropped `done` so the
      //    label can't strand on a tool that already completed.
      // Durable calls share the tool-call id with the stream, so this lines up.
      if (summary) {
        if (!activeSteps.has(id)) activeSteps.set(id, summary);
      } else {
        activeSteps.delete(id);
      }
      refreshStatus();
    },
    onConnection: (state, attempt) => {
      // Brief drops are normal (a permission wait, a dev-server reload) and the
      // bridge reconnects on its own — stay quiet unless reconnection is actually
      // struggling (several seconds of failed attempts). The disconnect shows as a
      // TRANSIENT notice line in the bottom region that clears itself the moment we
      // reconnect, so nothing about it lingers in the transcript.
      if (state === "reconnecting") {
        if (attempt >= 4) tui.setConnected(false);
      } else {
        tui.setConnected(true);
      }
    },
    requestApproval: async (req, summary, risk) => {
      // Boxed permission dialog (allow / always / deny / deny-with-feedback).
      // Feedback text rides back to the agent on the deny tool result.
      return tui.approval(
        `${summary}${req.requestPermission ? `\nwhy: ${req.requestPermission}` : ""}`,
        risk
      );
    },
  });

  // The message stream drives two live signals (the transcript itself is
  // rendered from polling for robustness):
  //  - `generation` events → the live output-token count (moves during ALL
  //    generation, including big tool arguments like file content).
  //  - `tool_call_started` / `tool_call_done` → the current step label.
  const stream = new MessageStream(api, threadId, {
    // Live streaming preview: answer text and (opt-in) internal reasoning feed
    // the TUI's ephemeral preview; the committed message still renders from
    // polling, which calls tui.clearStream() first so there's no double-render.
    onChunk: (text, mid) => tui.streamResponseDelta(text, mid),
    onReasoningChunk: (text, mid) => tui.streamThinkingDelta(text, mid),
    onAssistantText: () => {},
    onEvent: (eventType, data) => {
      if (eventType === "generation" && typeof data?.outputTokens === "number") {
        liveOut = data.outputTokens;
        refreshStatus();
      } else if (eventType === "tool_call_started" && data?.id) {
        activeSteps.set(data.id, data.progress || data.name || "working");
        refreshStatus();
      } else if (eventType === "tool_call_done" && data?.id) {
        activeSteps.delete(data.id);
        refreshStatus();
      } else if (eventType === "goal_updated" && data) {
        tui.setGoal(data);
      }
    },
    // A failed turn whose message is the lease service's at-limit denial → offer
    // an in-terminal upgrade right where the friction happened. (offerUpgrade /
    // isSessionLimitError are defined below; this callback only fires mid-session.)
    onError: (err: string) => {
      if (isSessionLimitError(err)) void offerUpgrade({ auto: true });
    },
  });

  // ── subagent tracking ──────────────────────────────────────────────────────
  // A subagent runs in its own child thread, but whether it's *actively running*
  // (vs idle or terminated) lives in our thread's child registry — not on the
  // thread-registry events. So we treat the system-events channel purely as a
  // "something changed" signal and re-read /api/threads/:id/subagents, showing a
  // line only for subagents whose status is `running`.
  // Running child threadId -> display title, registry status detail, and raw
  // agent name (the TUI colors the compaction agent orange by name).
  const activeSubagents = new Map<string, { title: string; registryDetail: string; agentName?: string }>();
  // Agent name → title, loaded once so subagent lines read "Research Assistant"
  // rather than the raw agent name. Best effort; falls back to a prettified name.
  const agentTitles = new Map<string, string>();
  const agentTitlesReady = api
    .listAgents()
    .then((list) => list.forEach((a) => agentTitles.set(a.name, a.title)))
    .catch(() => {});
  // Compose each line as "Title — activity". The live stream phrase (what the
  // subagent's own thread is doing RIGHT NOW — reading a page, running a
  // search) wins over the registry status text (setStatus heartbeats, which
  // only some agents publish); with neither, the bare title renders as before.
  const pushSubagents = () =>
    tui.setSubagents(
      [...activeSubagents.entries()].map(([id, s]) => {
        const detail = subActivity.phraseFor(id) ?? s.registryDetail;
        return { id, label: `${s.title}${detail ? ` — ${detail}` : ""}`, agentName: s.agentName };
      })
    );
  // Live per-subagent activity: one read-only stream to each running child
  // thread (opened/closed by the reconcile below via sync()); a phrase change
  // just re-pushes the lines.
  const subActivity = new SubagentActivity(api, () => pushSubagents());
  // Rebuild the running-subagent set from the authoritative child registry.
  // Runs on every (re)connect (covering subagents that started before this
  // socket, or during a reconnect gap) and whenever a child thread changes.
  const reconcileSubagents = async () => {
    try {
      await agentTitlesReady;
      const subs = await api.listSubagents(threadId);
      activeSubagents.clear();
      for (const s of subs) {
        // Hide finished/parked children. Anything else is live: "running", or a
        // custom working status (agents can publish progress via setStatus /
        // sessionStatus bindings — e.g. the coordinator's code workers heartbeat
        // "editing src/auth.ts — 2 of 3 call sites done" into the registry).
        const status = (s.status || "").trim();
        if (status === "idle" || status === "terminated") continue;
        // Keep the registry status text when it's more than the bare lifecycle
        // marker — it's the fallback detail when no live stream phrase exists.
        // Collapsed to one line — agents publish arbitrary status text, and a
        // newline inside a HUD row would desync the TUI's redraw math.
        const oneLineStatus = status.replace(/\s+/g, " ");
        activeSubagents.set(s.id, {
          title: subagentLabel(s, agentTitles),
          registryDetail: oneLineStatus && oneLineStatus !== "running" ? oneLineStatus.slice(0, 80) : "",
          agentName: s.agent_name ?? undefined,
        });
      }
      // Streams follow the registry: open for newcomers, closed for departed.
      subActivity.sync(activeSubagents.keys());
      pushSubagents();
    } catch {
      // Best effort — the next event or reconnect re-reconciles.
    }
  };
  // Coalesce bursts of events into a single registry read; if more events land
  // while a read is in flight, run exactly one more afterwards.
  let reconcileTimer: ReturnType<typeof setTimeout> | null = null;
  let reconcilePending = false;
  const scheduleReconcile = () => {
    if (reconcileTimer) {
      reconcilePending = true;
      return;
    }
    reconcileTimer = setTimeout(async () => {
      reconcileTimer = null;
      await reconcileSubagents();
      if (reconcilePending) {
        reconcilePending = false;
        scheduleReconcile();
      }
    }, 150);
  };
  const events = new SystemEvents(api, {
    onOpen: () => scheduleReconcile(),
    onThreadCreated: (t) => {
      if (t.parent === threadId) scheduleReconcile();
    },
    onThreadUpdated: (t) => {
      if (t.parent === threadId) scheduleReconcile();
    },
    onThreadDeleted: (id) => {
      if (activeSubagents.has(id)) scheduleReconcile();
    },
  });
  // Registry status text (setStatus / sessionStatus heartbeats) changes without
  // firing a thread event, so while any subagent line is visible, refresh it on
  // a slow poll to keep the displayed working state current. Discovery of NEW
  // subagents still comes from the events above; this only keeps text fresh.
  const heartbeatPoll = setInterval(() => {
    if (activeSubagents.size > 0) scheduleReconcile();
  }, 5000);
  heartbeatPoll.unref();

  // `/clear` resolves this promise; runInteractive tears the session down and
  // returns, and the caller loops back in with a fresh thread. (Quit never
  // resolves it — quit exits the process directly.)
  let endSession!: () => void;
  const sessionEnded = new Promise<void>((r) => (endSession = r));

  const quit = async () => {
    tui.end();
    // Stop the agent's turn on the backend before we exit — the LLM loop runs
    // remotely, so without this it would keep working after the CLI disconnects.
    // Also kill any background processes this machine spawned: they're detached,
    // so without this they'd outlive the session. Fire both, tear down locally,
    // then wait briefly so they land (capped so quitting stays snappy even if
    // the instance is unreachable).
    const stopped = api.stop(threadId).catch(() => {});
    const procsStopped = host.stopAllLocalProcesses().catch(() => 0);
    bridge.close();
    stream.close();
    events.close();
    subActivity.closeAll();
    mcp.closeAll();
    const [, killed] = await Promise.race([
      Promise.all([stopped, procsStopped]),
      new Promise<[unknown, number]>((r) => setTimeout(() => r([undefined, 0]), 1500)),
    ]);
    farewell(killed);
    process.exit(0);
  };
  tui.setQuitHandler(quit);

  // `/logout`: forget the saved token for this instance, then shut down like a
  // normal quit (stop the turn + this machine's background processes). The next
  // launch lands on the sign-in screen.
  const logout = async () => {
    deleteCredential(api.origin);
    const instanceHost = api.origin.replace(/^https?:\/\//, "");
    tui.print(`${c.gray}Signed out — removed the saved token for ${c.teal}${instanceHost}${c.reset}${c.gray}. Run standardcode to sign in again.${c.reset}`);
    await quit();
  };
  const bgMgr: BgManager = {
    list: () => registry.list(),
    stop: async (id: string) => {
      await host.execute("background_process", { action: "stop", id });
      refreshBgCount();
    },
  };

  // ── MCP control surface (for the /mcp command + slash menu) ────────────────
  const mcpCtl: McpController = {
    configured: () => listMcpServers(),
    connectedNames: () => mcp.connectedNames(),
    catalog: () => mcp.catalog(),
    connect: async (cfg) => {
      try {
        const client = await mcp.connect(cfg);
        publishMcpCatalog();
        return { ok: true, tools: client.tools.length };
      } catch (e) {
        publishMcpCatalog();
        return { ok: false, error: e instanceof Error ? e.message : String(e) };
      }
    },
    disconnect: (name) => {
      mcp.disconnect(name);
      publishMcpCatalog();
    },
    add: async (cfg) => {
      saveMcpServer(cfg);
      return mcpCtl.connect(cfg);
    },
    // Seed the request into the main chat — the agent researches + installs it
    // there (using research_agent + the mcp tool's install action), visible in
    // the transcript.
    requestInstall: (query) => {
      void sendNow(
        `Install an MCP server for me: ${query}. Research the best one and its exact launch command, then install it.`
      );
    },
    remove: (name) => {
      mcp.disconnect(name);
      removeMcpServer(name);
      publishMcpCatalog();
    },
    setEnabled: (name, enabled) => setMcpServerEnabled(name, enabled),
  };

  // ── submit / queue / steer ─────────────────────────────────────────────────
  // Pasted images become real message attachments: the instance stores the
  // bytes in the thread filesystem and injects them into the LLM's vision
  // context, exactly like an image sent from the web UI. The `[#Image N]`
  // placeholders stay in the text so the model can reference them by name.
  const extFor = (mime: string): string =>
    ({ "image/png": "png", "image/jpeg": "jpg", "image/gif": "gif", "image/webp": "webp" })[mime] ?? "bin";
  const toAttachments = (images: PastedImage[]): MessageAttachment[] =>
    images.map((img) => ({ name: `image-${img.seq}.${extFor(img.mime)}`, mimeType: img.mime, data: img.data }));

  const sendNow = async (text: string, images: PastedImage[] = []) => {
    lastSent = { text, images };
    tui.printUserMessage(text);
    // Mark it shown so the poller (which now also renders user messages) doesn't
    // double-print our own send once the backend persists it.
    const key = text.trim();
    pendingSent.set(key, (pendingSent.get(key) ?? 0) + 1);
    try {
      await api.sendMessage(threadId, text, toAttachments(images));
    } catch (e) {
      const n = (pendingSent.get(key) ?? 1) - 1;
      if (n > 0) pendingSent.set(key, n);
      else pendingSent.delete(key); // send failed — don't suppress a future identical message
      tui.print(`${c.dim}failed to send: ${e instanceof Error ? e.message : String(e)}${c.reset}`);
      return;
    }
    interrupting = false; // a fresh turn — let the working indicator show again
    busy = true;
    tui.setWorking(true);
  };
  const flushQueued = async () => {
    if (!queued.length) return;
    const toSend = queued.splice(0);
    tui.setQueuedCount(0);
    for (const q of toSend) await sendNow(q.text, q.images);
  };

  // Manually kick off a background compaction. Spawns the non-blocking
  // compaction subagent server-side immediately (even while the thread is idle),
  // which then surfaces through the normal subagent display — no special CLI
  // message needed. Idempotent: a server-side lock prevents overlapping runs.
  const requestCompaction = async () => {
    try {
      await api.compact(threadId);
    } catch (err) {
      tui.print(`${c.red}✗${c.reset} couldn't start compaction: ${(err as Error).message}`);
    }
  };

  // /account — open the standardcode.ai dashboard, pre-authed when the
  // instance can mint a magic link for this thread's user (falls back to the
  // plain dashboard where they sign in by inbox link).
  const runAccountCommand = async () => {
    tui.print(`${c.gray}Opening your account…${c.reset}`);
    const link = await api.accountLink(threadId).catch(() => null);
    const target = link?.url ?? "https://standardcode.ai/account";
    openUrl(target);
    tui.print(
      link?.preauthed
        ? `${c.gray}→ account dashboard opened in your browser (signed in)${c.reset}`
        : `${c.gray}→ opened ${target} — sign in with your account email${c.reset}`
    );
  };

  // ── in-terminal parallel-session upgrade ──────────────────────────────────
  const ordinal = (n: number): string => {
    const s = ["th", "st", "nd", "rd"], v = n % 100;
    return `${n}${s[(v - 20) % 10] ?? s[v] ?? s[0]}`;
  };
  const fmtCost = (q: SessionQuote): string | null =>
    typeof q.amount_cents === "number" && q.amount_cents !== 0
      ? `$${(Math.abs(q.amount_cents) / 100).toFixed(2)}`
      : null;

  // A delightful, on-brand panel: gradient title, a session meter (● in use,
  // gradient ＋ for the one being added, dim · for headroom), and an unambiguous
  // cost line (with the amber trial-conversion warning when applicable).
  const renderUpgradePanel = (q: SessionQuote): string => {
    const dots: string[] = [];
    for (let i = 0; i < q.max; i++) {
      if (i < q.current) dots.push(`${c.teal}●${c.reset}`);
      else if (i === q.current) dots.push(`${c.bold}${gradientText("＋")}${c.reset}`);
      else dots.push(`${c.dim}·${c.reset}`);
    }
    const cost = fmtCost(q);
    const lines = [
      "",
      `${c.bold}${gradientText("✦ Add a parallel session")}${c.reset}`,
      "",
      `${dots.join(" ")}   ${c.dim}${q.current} of ${q.current} session${q.current === 1 ? "" : "s"} in use${c.reset}`,
    ];
    if (q.ends_trial) {
      lines.push(
        `${c.yellow}Your $5 trial covers 1 session. Adding a ${ordinal(q.sessions)} ends the trial${c.reset}`,
        `${c.yellow}and starts your $49/mo plan now${cost ? ` — ${c.bold}${cost} charged today${c.reset}${c.yellow}` : ""}.${c.reset}`,
      );
    } else if (cost) {
      lines.push(`Add a ${ordinal(q.sessions)} session at $49/mo — ${c.bold}${cost} charged now${c.reset}.`);
    } else {
      lines.push(`Add a ${ordinal(q.sessions)} session at $49/mo — prorated on your next invoice.`);
    }
    lines.push("");
    return lines.join("\n");
  };

  // Offer + apply an upgrade. `auto` = triggered by hitting the limit mid-turn
  // (we then resend the blocked message so the session just continues).
  const offerUpgrade = async (opts: { auto?: boolean } = {}) => {
    if (upgradeInFlight) return;
    upgradeInFlight = true;
    try {
      // Say WHY this appeared before doing anything else. When triggered by a
      // blocked turn (auto), the raw denial was deliberately swallowed — with
      // no explanation the browser opening reads as a glitch, not a limit.
      if (opts.auto) {
        tui.print(
          `${c.yellow}You're out of simultaneous sessions — another Standard Code session is using your slot.${c.reset}`
        );
      }
      const quote = await api.sessionsQuote(threadId);
      if (!quote) {
        // No subscription or billing unreachable → hand off to the dashboard.
        const link = await api.accountLink(threadId).catch(() => null);
        const target = link?.url ?? "https://standardcode.ai/account";
        tui.print(
          `${c.gray}Close the other session (its slot frees within ~90s) — or add another` +
            ` simultaneous session to your plan, then resend your message.${c.reset}`
        );
        openUrl(target);
        tui.print(`${c.gray}→ opened ${target} to manage your plan${c.reset}`);
        return;
      }
      if (quote.current >= quote.max) {
        tui.print(
          `${c.yellow}You're at the maximum of ${quote.max} parallel session${quote.max === 1 ? "" : "s"}.${c.reset}\n` +
            `${c.gray}Close another session (its slot frees within ~90s), then resend your message.${c.reset}`
        );
        return;
      }
      tui.print(renderUpgradePanel(quote));
      const cost = fmtCost(quote);
      const confirmLabel = quote.ends_trial
        ? `End trial & start now${cost ? ` — pay ${cost} today` : ""}`
        : `Confirm${cost ? ` — pay ${cost} now` : " — add the session"}`;
      const choice = await tui.select(`Add a ${ordinal(quote.sessions)} parallel session?`, [
        { label: confirmLabel, value: "go" as const },
        { label: "Not now", value: "no" as const },
      ]);
      if (choice !== "go") {
        tui.print(`${c.gray}No change made — you can upgrade anytime with /upgrade.${c.reset}`);
        return;
      }
      tui.print(`${c.gray}Applying…${c.reset}`);
      let applied: { ok?: boolean; sessions?: number; error?: string };
      try {
        applied = await api.sessionsUpgrade(threadId, quote.sessions);
      } catch (e) {
        tui.print(`${c.red}✗${c.reset} Upgrade failed: ${e instanceof Error ? e.message : String(e)}`);
        return;
      }
      if (!applied?.ok) {
        tui.print(`${c.red}✗${c.reset} Upgrade failed: ${applied?.error ?? "unknown error"}`);
        return;
      }
      const n = applied.sessions ?? quote.sessions;
      tui.print(`${c.green}✓${c.reset} ${c.bold}${gradientText(`You now have ${n} parallel session${n === 1 ? "" : "s"}.`)}${c.reset}`);
      // Continue the blocked turn: resend the message that hit the limit.
      if (opts.auto && lastSent) {
        tui.print(`${c.gray}Continuing…${c.reset}`);
        await sendNow(lastSent.text, lastSent.images);
      }
    } finally {
      upgradeInFlight = false;
    }
  };

  // Recognize the lease service's at-limit / no-subscription denials (surfaced
  // as the hook's blocked-turn error text) so we can offer an upgrade in place.
  const isSessionLimitError = (msg: string): boolean =>
    /thread limit reached|simultaneous (thread|session)s?.*in use|no active standard code subscription/i.test(msg);

  // ── skills control surface (for the /skills command) ──────────────────────
  // Skills live in the INSTANCE (global across sessions); the CLI only lists
  // and toggles them over the API — nothing skill-related is stored locally.
  const skillsCtl: SkillsController = {
    list: () => api.listSkills(),
    setEnabled: (name, enabled) => api.setSkillEnabled(name, enabled),
    remove: (name) => api.removeSkill(name),
    // Seed the request into the main chat — the agent researches or authors
    // the skill there (research_agent + the skill tool's install action),
    // visible in the transcript.
    requestInstall: (query) => {
      void sendNow(
        `Install a skill for me: ${query}. Find the skill's published files (or author a proper SKILL.md from your research), install it, then tell me what it can do.`
      );
    },
  };

  // Slash-command palette: typing "/" filters this list inline above the prompt.
  // Each command is individually addressable (/compact, /mcp, …). Hints are
  // functions so live state (context %, level, counts) stays fresh as it redraws.
  tui.setCommands([
    {
      name: "clear",
      label: "Clear conversation",
      hint: "start a fresh session",
      run: () => endSession(),
    },
    {
      name: "compact",
      label: "Compact conversation now",
      hint: () => tui.contextPctLabel() || "free up context",
      run: requestCompaction,
    },
    { name: "level", label: "Auto-accept level", hint: () => `level ${tui.level}`, run: () => runLevelMenu(tui, perm) },
    {
      name: "permissions",
      label: "Approved commands",
      hint: () => {
        const n = perm.alwaysAllow.size + perm.allowRisk.size;
        return n ? `${n} approved` : "none";
      },
      run: () => runApprovalsMenu(tui, perm, () => saveApprovals(api, threadId, perm)),
    },
    {
      name: "mcp",
      label: "MCP servers",
      hint: () => {
        const n = mcpCtl.connectedNames().length;
        return n ? `${n} connected` : "none";
      },
      run: () => runMcpMenu(tui, mcpCtl),
    },
    {
      name: "skills",
      label: "Agent skills",
      hint: "list / install / manage",
      run: () => runSkillsMenu(tui, skillsCtl),
    },
    {
      name: "account",
      label: "Manage your account",
      hint: "billing & sessions, opens in browser",
      run: () => runAccountCommand(),
    },
    {
      name: "upgrade",
      label: "Add a parallel session",
      hint: "run more sessions at once",
      run: () => offerUpgrade(),
    },
    { name: "background", label: "Background processes", hint: "list / stop", run: () => runProcessMenu(tui, bgMgr) },
    { name: "keybindings", label: "Keyboard shortcuts", run: () => showKeybindings(tui) },
    { name: "update", label: "Check for updates", hint: "check for a newer version", run: () => runUpdateCommand(tui) },
    { name: "logout", label: "Sign out", hint: "delete the saved token & quit", run: () => logout() },
    { name: "quit", label: "Quit", run: () => quit() },
  ]);

  // Seed ↑/↓ recall from the thread KV (inheriting the project's newest prior
  // thread on a fresh session); every submit appends (whether it sends now or
  // queues) so history survives across sessions — all server-side, nothing on
  // the local filesystem.
  const history = await loadHistory(api, threadId, historySeedThreadId);
  tui.setHistory(history);

  tui.onSubmit = (text, images) => {
    appendHistory(api, threadId, history, text);
    if (editingQueued) {
      editingQueued = false;
      queued.push({ text, images });
      tui.setQueuedCount(queued.length);
      tui.print(`${c.gray}⏳ queued:${c.reset} ${text}`);
      return;
    }
    if (busy) {
      queued.push({ text, images });
      tui.setQueuedCount(queued.length);
      tui.print(`${c.gray}⏳ queued:${c.reset} ${text}  ${c.dim}(esc to steer now)${c.reset}`);
    } else {
      void sendNow(text, images);
    }
  };

  tui.onInterrupt = () => {
    if (queued.length > 0) {
      tui.print(`${c.yellow}↪ steering — stopping current work and sending your message…${c.reset}`);
      void api
        .stop(threadId)
        .catch(() => {})
        .then(() => flushQueued());
    } else if (busy) {
      // Optimistically stop the working indicator so esc feels immediate.
      interrupting = true;
      busy = false;
      activeSteps.clear();
      liveOut = 0;
      tui.setWorking(false);
      refreshStatus();
      tui.print(`${c.yellow}[interrupted by user]${c.reset}`);
      void api.stop(threadId).catch(() => {});
    }
  };

  // Enter on the ←-selected [⚙ n bg] prompt badge opens the same panel as
  // the /background slash command.
  tui.onBgBadge = () => {
    void runProcessMenu(tui, bgMgr).catch(() => {});
  };

  // Up on an empty top line first pulls the last QUEUED message back for
  // editing (returning true consumes the key); otherwise the TUI falls through
  // to history recall.
  tui.onUpArrow = () => {
    if (tui.getInput().trim() || queued.length === 0) return false;
    const q = queued.pop()!;
    tui.setQueuedCount(queued.length);
    editingQueued = true;
    tui.setInput(q.text, q.images);
    return true;
  };

  events.connect();
  await Promise.all([bridge.connect(), stream.connect()]);

  // Seed the live goal checklist; it then stays current via goal_updated events.
  void api.getGoal(threadId).then((g) => tui.setGoal(g)).catch(() => {});

  attaching.stop();
  tui.banner([
    `${c.bold}${c.magenta}Standard Code${c.reset} ${c.dim}— coding agent${c.reset}`,
    `${c.gray}project:${c.reset} ${projectDir}`,
    `${c.gray}machine:${c.reset} ${machine}   ${c.gray}thread:${c.reset} ${threadId.slice(0, 8)}`,
  ]);

  if (resumed) await printHistory(api, threadId, tui);

  // Seed shown-ids so the poller doesn't re-print existing history.
  try {
    (await api.getMessages(threadId, 200)).forEach((m: any) => shownIds.add(m.id));
  } catch {
    // ignore
  }

  const runningProcs = (await registry.list()).filter((p) => p.status === "running");
  if (runningProcs.length) {
    tui.print(
      `${c.cyan}⚙ ${runningProcs.length} background process${runningProcs.length === 1 ? "" : "es"} running:${c.reset}`
    );
    for (const p of runningProcs) tui.print(`${c.gray}  ${p.id}  ${p.description || p.command}${c.reset}`);
  }
  refreshBgCount();

  // Connect every enabled MCP server, then publish the catalog so the agent sees
  // the tools on its very first step. (The CLI is the MCP host; this is the real
  // initialize/tools-list handshake with each local server.)
  const enabledServers = listMcpServers().filter((s) => s.enabled);
  for (const s of enabledServers) {
    const res = await mcpCtl.connect(s);
    if (res.ok) {
      tui.print(`${c.cyan}⚡ MCP "${s.name}" connected${c.reset} ${c.gray}(${res.tools} tool${res.tools === 1 ? "" : "s"})${c.reset}`);
    } else {
      tui.print(`${c.red}⚠ MCP "${s.name}" failed:${c.reset} ${c.gray}${res.error}${c.reset}`);
    }
  }
  publishMcpCatalog();

  tui.start(); // begin the persistent input line

  // ── poller: display new messages, drive the working indicator, flush queue ──
  const poll = async () => {
    let msgs: any[];
    try {
      msgs = await api.getMessages(threadId, 60);
    } catch {
      return;
    }
    const sorted = [...msgs].sort((a, b) => (a.created_at ?? 0) - (b.created_at ?? 0));
    for (const m of sorted) {
      if (shownIds.has(m.id) || m.status === "pending") continue;
      shownIds.add(m.id);
      const text = messageText(m.content).trim();
      // At-limit / no-subscription denial (from the enforce_thread_leases hook)
      // → offer the in-terminal upgrade instead of printing the raw error. The
      // stream's onError catches the same condition; upgradeInFlight dedups.
      const denial = (typeof m.error === "string" && m.error) || text;
      if (denial && isSessionLimitError(denial)) {
        void offerUpgrade({ auto: true });
        continue;
      }
      if (m.role === "assistant" && text) printAssistant(tui, text);
      else if (m.role === "system" && text) tui.print(`${c.dim}${text}${c.reset}`);
      else if (m.role === "user" && text) {
        const pending = pendingSent.get(text) ?? 0;
        if (pending > 0) {
          // Our own optimistic send, now confirmed — already on screen.
          if (pending === 1) pendingSent.delete(text);
          else pendingSent.set(text, pending - 1);
        } else {
          // Submitted from elsewhere — render it as a normal sent user message.
          tui.printUserMessage(text);
        }
      }
    }
    const polledBusy = threadBusy(msgs);
    // While interrupting, keep the indicator off until the backend has actually
    // gone idle — then clear the latch so future turns show "Working" again.
    if (interrupting) {
      if (!polledBusy) interrupting = false;
      busy = false;
    } else {
      busy = polledBusy;
    }
    tui.setWorking(busy);
    if (!busy) {
      // Turn finished — clear any stale step + in-progress live count.
      if (activeSteps.size) activeSteps.clear();
      liveOut = 0;
      refreshStatus();
      if (queued.length > 0 && !editingQueued) await flushQueued();
    }
    refreshBgCount();

    // Authoritative token totals from completed LLM-call logs.
    try {
      const logs = await api.getLogs(threadId, 100);
      let landed = 0;
      for (const l of logs) {
        // Skip until the call is finalized — an in-progress log has null tokens,
        // and counting it early would mark its id seen before the real counts land.
        if (!l.is_complete) continue;
        const id = l.id ?? `${l.created_at}:${l.total_tokens}`;
        if (countedLogs.has(id)) continue;
        countedLogs.add(id);
        const inT = Number(l.input_tokens) || 0;
        const outT = Number(l.output_tokens) || 0;
        tokensIn += inT;
        tokensOut += outT;
        landed += outT;
      }
      if (landed > 0) liveOut = 0; // a real count arrived; drop the live estimate
      refreshStatus();
    } catch {
      // ignore — token feed is best-effort
    }

    // Context gauge: the runtime records the latest request's input tokens
    // (the true serialized context size) and the model's window to thread KV.
    // Scaled to the COMPACTION POINT, not the raw window: the instance triggers
    // background compaction at half the model's context (its
    // TRIGGER_CONTEXT_FRACTION), so 100% here = "compaction is about to run",
    // which is the only ceiling the user can actually hit.
    const COMPACTION_TRIGGER_FRACTION = 0.5;
    // Mirrors the instance's TRIGGER_TOKENS_CAP: compaction fires at
    // min(50% of the window, 50k tokens), so the gauge tops out there too.
    const COMPACTION_TRIGGER_CAP = 50_000;
    try {
      const cu = (await api.kvGet(threadId, "context_usage")) as
        | { inputTokens?: number; maxContextTokens?: number }
        | null;
      const used = Number(cu?.inputTokens) || 0;
      const max = Math.min(
        (Number(cu?.maxContextTokens) || 0) * COMPACTION_TRIGGER_FRACTION || Infinity,
        COMPACTION_TRIGGER_CAP,
      );
      tui.setContextPct(max > 0 && used > 0 ? (used / max) * 100 : null);
    } catch {
      // ignore — gauge is best-effort
    }
  };
  const pollTimer = setInterval(() => void poll().catch(() => {}), 1200);

  // Event-driven from here. The promise resolves only on `/clear` — quit exits
  // the process without ever resolving it.
  await sessionEnded;

  // ── /clear teardown: silence this session so the next one owns the TUI ────
  clearInterval(pollTimer);
  clearInterval(heartbeatPoll);
  // Stop the remote turn (the LLM loop runs server-side) and this machine's
  // background processes — the fresh thread's registry won't know about them.
  const stopped = api.stop(threadId).catch(() => {});
  const procsStopped = host.stopAllLocalProcesses().catch(() => 0);
  bridge.close();
  stream.close();
  events.close();
  subActivity.closeAll();
  mcp.closeAll();
  const [, killed] = await Promise.race([
    Promise.all([stopped, procsStopped]),
    new Promise<[unknown, number]>((r) => setTimeout(() => r([undefined, 0]), 1500)),
  ]);
  // Reset the live bottom-region signals so nothing from the old thread lingers
  // while the new session boots and re-seeds them.
  tui.setWorking(false);
  tui.setSubagents([]);
  tui.setGoal(null);
  tui.setQueuedCount(0);
  tui.setContextPct(null);
  tui.setStep(null, 0);
  tui.setBackgroundCount(0);
  if (killed > 0) {
    tui.print(`${c.cyan}⚙${c.reset} Stopped ${killed} background process${killed === 1 ? "" : "es"}.`);
  }
  tui.print(`${c.dim}── conversation cleared — starting a fresh session ──${c.reset}`);
}

interface BgManager {
  list: () => Promise<{ id: string; command: string; description?: string; status: string; pid: number; exitCode?: number | null }[]>;
  stop: (id: string) => Promise<void>;
}

interface SkillsController {
  list: () => Promise<SkillInfo[]>;
  setEnabled: (name: string, enabled: boolean) => Promise<void>;
  remove: (name: string) => Promise<void>;
  /** Seed an install request into the main chat for the agent to research + install. */
  requestInstall: (query: string) => void;
}

/**
 * Interactive skills control surface (reached via `/skills`). Lists installed
 * skills with enabled state, and lets you install (free-text request handled
 * by the agent in-chat), enable/disable, inspect, and remove — all menu-driven.
 */
async function runSkillsMenu(tui: Tui, skills: SkillsController): Promise<void> {
  let list: SkillInfo[];
  try {
    list = await skills.list();
  } catch (e) {
    tui.print(`${c.red}✗ couldn't load skills:${c.reset} ${c.gray}${e instanceof Error ? e.message : String(e)}${c.reset}`);
    return;
  }

  const INSTALL = "__install__";
  const items = list.map((s) => ({
    label: s.name,
    hint: `${s.enabled ? "enabled" : "disabled"} · ${s.files.length} file${s.files.length === 1 ? "" : "s"}`,
    value: s.name,
  }));
  items.push({ label: "＋ Install a skill…", hint: "find & install", value: INSTALL });

  const picked = await tui.select(
    `${c.bold}Agent skills${c.reset}  ${c.dim}(↑/↓ · enter · esc to close)${c.reset}`,
    items
  );
  if (!picked) return;

  if (picked === INSTALL) {
    const query = await tui.prompt(
      "What skill do you want to install?",
      "the anthropic pdf skill / a skill for writing conventional commits"
    );
    if (query) skills.requestInstall(query);
    return;
  }

  const skill = list.find((s) => s.name === picked)!;
  tui.print(`${c.cyan}${skill.name}${c.reset}${skill.version ? ` ${c.dim}v${skill.version}${c.reset}` : ""} ${c.gray}— ${skill.description}${c.reset}`);
  const action = await tui.select(`${c.bold}${picked}${c.reset}`, [
    skill.enabled
      ? { label: "Disable (hide from the agent)", value: "disable" as const }
      : { label: "Enable", value: "enable" as const },
    { label: "View files", value: "files" as const },
    { label: "Remove this skill", value: "remove" as const },
    { label: "Back", value: "back" as const },
  ]);

  try {
    if (action === "enable" || action === "disable") {
      await skills.setEnabled(picked, action === "enable");
      tui.print(`${c.gray}${action}d ${picked}${c.reset}`);
    } else if (action === "files") {
      for (const f of skill.files) tui.print(`  ${c.gray}${f}${c.reset}`);
    } else if (action === "remove") {
      await skills.remove(picked);
      tui.print(`${c.gray}removed ${picked}${c.reset}`);
    }
  } catch (e) {
    tui.print(`${c.red}✗ ${e instanceof Error ? e.message : String(e)}${c.reset}`);
  }
}

interface McpController {
  configured: () => McpServerConfig[];
  connectedNames: () => string[];
  catalog: () => { servers: { name: string; status: string; tools: { name: string; description?: string }[]; resources: { uri: string }[]; error?: string }[] };
  connect: (cfg: McpServerConfig) => Promise<{ ok: boolean; tools?: number; error?: string }>;
  disconnect: (name: string) => void;
  add: (cfg: McpServerConfig) => Promise<{ ok: boolean; tools?: number; error?: string }>;
  remove: (name: string) => void;
  setEnabled: (name: string, enabled: boolean) => void;
  /** Seed an install request into the main chat for the agent to research + install. */
  requestInstall: (query: string) => void;
}

/** The `/` configuration menu: clear, permission mode, approvals, processes, shortcuts, sign out, quit. */
/** The auto-accept level submenu (reached via `/level`). */
async function runLevelMenu(tui: Tui, perm: PermissionState): Promise<void> {
  const picked = await tui.select(
    `${c.bold}Auto-accept level${c.reset}  ${c.dim}(↑/↓ · enter · shift-tab cycles)${c.reset}`,
    LEVELS.map((l) => ({
      label: levelLabel(l),
      hint: l === tui.level ? "current" : "",
      value: l,
    }))
  );
  if (picked) {
    tui.setLevel(picked);
    perm.level = picked;
  }
}

/** Print the keyboard-shortcut reference (reached via `/keybindings`). */
function showKeybindings(tui: Tui): void {
  tui.print(`${c.gray}shortcuts:${c.reset}`);
  tui.print(`${c.gray}  shift-tab${c.reset}  cycle auto-accept level (1–5)`);
  tui.print(`${c.gray}  /${c.reset}          open the command palette (type to filter)`);
  tui.print(`${c.gray}  ctrl-v${c.reset}     paste an image from the clipboard ([#Image 1])`);
  tui.print(`${c.gray}  ↑ / ↓${c.reset}      cycle past messages (on the input's top line)`);
  tui.print(`${c.gray}  ←${c.reset}          from the start of the input: select the [⚙ n bg] badge (enter opens it)`);
  tui.print(`${c.gray}  ctrl-c${c.reset}     quit`);
}

/** Check npm for a newer version and prompt to update. */
async function runUpdateCommand(tui: Tui): Promise<void> {
  const version = readVersion();
  const result = await forceCheckForUpdate(version);
  if (!result) {
    tui.print(`${c.green}✓${c.reset} ${c.gray}@standardagents/code${c.reset} is up to date (v${version})`);
    return;
  }
  const { latest } = result;
  tui.print(`\n  ${c.yellow}⟳${c.reset} Update available: ${c.gray}v${version}${c.reset} → ${c.green}v${latest}${c.reset}`);
  // Update through the package manager that owns this install — updating a
  // pnpm/bun/yarn-managed global with npm would fork it into competing copies.
  const pm = detectPackageManager();
  if (!pm) {
    tui.print(`  ${c.gray}This is a source checkout — pull the repo to update.${c.reset}`);
    return;
  }
  const { display } = updateCommand(pm);
  const choice = await tui.select(`Update now with \`${display}\`?`, [
    { label: "Yes, update now", value: "yes" as const },
    { label: "No, skip", value: "no" as const },
  ]);
  if (choice === "yes") {
    tui.print(`  ${c.gray}Running ${display}…${c.reset}`);
    const { ok, output: pmOutput } = await runUpdate(pm);
    if (ok) {
      tui.print(`  ${c.green}✓${c.reset} Updated to v${latest}. Restart to use the new version.`);
    } else {
      tui.print(`  ${c.red}✗${c.reset} Update failed:`);
      for (const line of pmOutput.trim().split("\n").slice(-6)) {
        tui.print(`  ${c.dim}${line}${c.reset}`);
      }
    }
  } else {
    tui.print(`  ${c.gray}Skipped. Run /update later.${c.reset}`);
  }
}

/** Submenu listing tracked background processes; lets you stop a running one. */
async function runProcessMenu(tui: Tui, bg: BgManager): Promise<void> {
  const procs = await bg.list();
  if (!procs.length) {
    tui.print(`${c.gray}No background processes for this session.${c.reset}`);
    return;
  }
  const items = procs.map((p) => {
    const status =
      p.status === "running"
        ? `${c.green}running${c.reset}`
        : `${c.gray}${p.status}${typeof p.exitCode === "number" ? ` (exit ${p.exitCode})` : ""}${c.reset}`;
    return {
      label: `${p.description || p.command}`,
      hint: `${p.id} · ${status}`,
      value: p.id,
    };
  });
  const picked = await tui.select(
    `${c.bold}Background processes${c.reset}  ${c.dim}(↑/↓ · enter to manage · esc to close)${c.reset}`,
    items
  );
  if (!picked) return;
  const proc = procs.find((p) => p.id === picked);
  if (!proc || proc.status !== "running") {
    tui.print(`${c.gray}${picked} is not running.${c.reset}`);
    return;
  }
  const action = await tui.select(`${c.bold}${proc.description || proc.command}${c.reset}`, [
    { label: "Stop this process", value: "stop" as const },
    { label: "Leave it running", value: "leave" as const },
  ]);
  if (action === "stop") {
    await bg.stop(picked);
    tui.print(`${c.gray}stopped ${picked}${c.reset}`);
  }
}

/** Submenu showing the session's pre-approved tools/risk levels; pick one to revoke. */
async function runApprovalsMenu(tui: Tui, perm: PermissionState, save: () => void): Promise<void> {
  const tools = Array.from(perm.alwaysAllow).sort();
  const risks = Array.from(perm.allowRisk).sort((a, b) => a - b);
  if (!tools.length && !risks.length) {
    tui.print(
      `${c.gray}No pre-approved commands. At a permission prompt, choose "Always allow this tool" or "Allow all level N" to add some.${c.reset}`
    );
    return;
  }
  const items = [
    ...tools.map((t) => ({ label: `Tool: ${t}`, hint: "always allowed", value: `tool:${t}` })),
    ...risks.map((r) => ({ label: `Risk level ${r} and below`, hint: "auto-accepted", value: `risk:${r}` })),
    { label: "Clear all approvals", hint: "", value: "clear" },
  ];
  const picked = await tui.select(
    `${c.bold}Approved commands${c.reset}  ${c.dim}(enter to revoke · esc to close)${c.reset}`,
    items
  );
  if (!picked) return;
  if (picked === "clear") {
    perm.alwaysAllow.clear();
    perm.allowRisk.clear();
    tui.print(`${c.gray}cleared all approvals${c.reset}`);
  } else if (picked.startsWith("tool:")) {
    const t = picked.slice(5);
    perm.alwaysAllow.delete(t);
    tui.print(`${c.gray}revoked tool ${t}${c.reset}`);
  } else if (picked.startsWith("risk:")) {
    const r = Number(picked.slice(5));
    perm.allowRisk.delete(r);
    tui.print(`${c.gray}revoked level ${r}${c.reset}`);
  }
  save();
}

/**
 * Interactive MCP control surface (reached via `/` → "MCP servers"). Lists
 * configured servers with status, and lets you add (free-text command), view
 * tools, connect/disconnect, enable/disable, and remove — all menu-driven.
 */
async function runMcpMenu(tui: Tui, mcp: McpController): Promise<void> {
  const configured = mcp.configured();
  const connected = new Set(mcp.connectedNames());
  const cat = mcp.catalog();

  const INSTALL = "__install__";
  const ADD_MANUAL = "__manual__";
  const items = configured.map((s) => {
    const entry = cat.servers.find((e) => e.name === s.name);
    const status = !s.enabled
      ? "disabled"
      : connected.has(s.name)
        ? `connected · ${entry?.tools.length ?? 0} tools`
        : entry?.error
          ? "error"
          : "disconnected";
    return { label: s.name, hint: status, value: s.name };
  });
  items.push({ label: "＋ Install a new MCP server…", hint: "find & install", value: INSTALL });
  items.push({ label: "Add manually (name: command)…", hint: "advanced", value: ADD_MANUAL });

  const picked = await tui.select(
    `${c.bold}MCP servers${c.reset}  ${c.dim}(↑/↓ · enter · esc to close)${c.reset}`,
    items
  );
  if (!picked) return;

  if (picked === INSTALL) {
    await installMcpServerFlow(tui, mcp);
    return;
  }
  if (picked === ADD_MANUAL) {
    await addMcpServer(tui, mcp);
    return;
  }

  const server = configured.find((s) => s.name === picked)!;
  const isConnected = connected.has(picked);

  const action = await tui.select(`${c.bold}${picked}${c.reset}`, [
    { label: "View tools", value: "tools" as const },
    isConnected
      ? { label: "Disconnect", value: "disconnect" as const }
      : { label: "Connect", value: "connect" as const },
    server.enabled
      ? { label: "Disable (don't auto-connect)", value: "disable" as const }
      : { label: "Enable (auto-connect on start)", value: "enable" as const },
    { label: "Remove this server", value: "remove" as const },
    { label: "Back", value: "back" as const },
  ]);

  if (action === "tools") {
    const entry = mcp.catalog().servers.find((e) => e.name === picked);
    if (!entry || entry.status !== "connected") {
      tui.print(`${c.gray}${picked} is not connected — connect it to list tools.${c.reset}`);
      return;
    }
    if (!entry.tools.length) tui.print(`${c.gray}${picked} exposes no tools.${c.reset}`);
    for (const t of entry.tools) tui.print(`  ${c.cyan}${t.name}${c.reset}${t.description ? ` ${c.gray}— ${t.description}${c.reset}` : ""}`);
    if (entry.resources.length) tui.print(`  ${c.gray}${entry.resources.length} resource(s)${c.reset}`);
  } else if (action === "connect") {
    const res = await mcp.connect(server);
    tui.print(res.ok ? `${c.cyan}⚡ connected (${res.tools} tools)${c.reset}` : `${c.red}⚠ ${res.error}${c.reset}`);
  } else if (action === "disconnect") {
    mcp.disconnect(picked);
    tui.print(`${c.gray}disconnected ${picked}${c.reset}`);
  } else if (action === "enable") {
    mcp.setEnabled(picked, true);
    const res = await mcp.connect(server);
    tui.print(res.ok ? `${c.cyan}⚡ enabled + connected (${res.tools} tools)${c.reset}` : `${c.red}⚠ enabled but failed: ${res.error}${c.reset}`);
  } else if (action === "disable") {
    mcp.setEnabled(picked, false);
    mcp.disconnect(picked);
    tui.print(`${c.gray}disabled + disconnected ${picked}${c.reset}`);
  } else if (action === "remove") {
    mcp.remove(picked);
    tui.print(`${c.gray}removed ${picked}${c.reset}`);
  }
}

/** Prompt for a new MCP server (`name: command args`), then save + connect it. */
async function addMcpServer(tui: Tui, mcp: McpController): Promise<void> {
  const spec = await tui.prompt(
    "Add an MCP server — enter  name: command [args…]",
    "fs: npx -y @modelcontextprotocol/server-filesystem ."
  );
  if (!spec) return;
  const cfg = parseServerSpec(spec);
  if (!cfg) {
    tui.print(`${c.yellow}couldn't parse that. Use  name: command [args]${c.reset}`);
    return;
  }
  tui.print(`${c.gray}connecting MCP "${cfg.name}" (${cfg.command} ${cfg.args.join(" ")})…${c.reset}`);
  const res = await mcp.add(cfg);
  if (res.ok) tui.print(`${c.cyan}⚡ MCP "${cfg.name}" connected${c.reset} ${c.gray}(${res.tools} tool${res.tools === 1 ? "" : "s"})${c.reset}`);
  else tui.print(`${c.red}⚠ MCP "${cfg.name}" failed:${c.reset} ${c.gray}${res.error}${c.reset} ${c.dim}(saved; retry from the MCP menu)${c.reset}`);
}

/**
 * The "Install a new MCP server" flow: ask what the user wants, then seed that
 * request into the main chat. The agent researches the right server (via
 * research_agent) and installs it (the mcp tool's install action) right there
 * in the transcript — so the same thing happens whether you use this menu or
 * just ask in chat. We land straight back in the conversation.
 */
async function installMcpServerFlow(tui: Tui, mcp: McpController): Promise<void> {
  const query = await tui.prompt(
    "What MCP server do you want to install?",
    "the best computer-use mcp server for mac"
  );
  if (!query) return;
  mcp.requestInstall(query);
}

main().catch((err) => {
  process.stderr.write(`\n${err instanceof Error ? err.stack || err.message : String(err)}\n`);
  process.exit(1);
});
